import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { map } from 'rxjs/internal/operators/map';
import { of } from 'rxjs/internal/observable/of';
import { shareReplay } from 'rxjs/internal/operators/shareReplay';
import { merge } from 'rxjs/internal/observable/merge';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { timer } from 'rxjs/internal/observable/timer';

export interface LoadingIcon {
  svg: string;
  label: string;
  color?: string;
}

@Injectable({ providedIn: 'root' })
export class LoadingOverlayService {

  private _pending = 0;
  private _pending$ = new BehaviorSubject<number>(0);

  // Collapses the request counter to a boolean, then:
  // - false→true: wait 200ms before showing (avoids flash on fast requests), then auto-hide after 5s
  // - true→false: hide immediately when all requests finish (cancels the 5s timer)
  readonly loading$ = this._pending$.pipe(
    map(n => n > 0),
    distinctUntilChanged(),
    switchMap(active => {
      if (!active) return of(false);
      return timer(200).pipe(
        switchMap(() => merge(
          of(true),
          timer(5000).pipe(map(() => false))
        ))
      );
    }),
    distinctUntilChanged(),
    shareReplay(1)
  );

  increment(): void { this._pending$.next(++this._pending); }
  decrement(): void { this._pending$.next(Math.max(0, --this._pending)); }

  private colors: string[] = [
    '#577fc0', // blue
    '#8b5cf6', // purple
    '#14b8a6', // teal
    '#f97316', // orange
    '#ec4899', // pink
    '#10b981', // emerald
    '#6366f1', // indigo
    '#d97706', // amber
    '#ef4444', // red-coral
    '#06b6d4', // cyan
  ];

  private icons: LoadingIcon[] = [
    {
      label: 'Brewing...',
      svg: this.wrap(`
        <path class="anim-steam s1" d="M16 14c0-3 3-4 3-7"/>
        <path class="anim-steam s2" d="M22 14c0-3 3-4 3-7"/>
        <path class="anim-steam s3" d="M28 14c0-3 3-4 3-7"/>
        <rect x="10" y="18" width="22" height="16" rx="3"/>
        <path d="M32 23h3a3 3 0 010 6h-3"/>
        <path d="M8 40h26" stroke-width="2.5"/>
      `)
    },
    {
      label: 'Launching...',
      svg: this.wrap(`
        <path d="M24 4c-5 8-6 18-6 26h12c0-8-1-18-6-26z"/>
        <circle cx="24" cy="19" r="3"/>
        <path d="M18 30l-5 8h5"/>
        <path d="M30 30l5 8h-5"/>
        <path class="anim-flame" d="M20 36q4 6 4 8 0-2 4-8"/>
      `)
    },
    {
      label: 'Almost there...',
      svg: this.wrap(`
        <path d="M24 6L8 40h32z"/>
        <path d="M10 38q14 4 28 0" stroke-width="3"/>
        <circle cx="21" cy="23" r="2.5" fill="currentColor" class="anim-pulse"/>
        <circle cx="28" cy="31" r="2.5" fill="currentColor" class="anim-pulse p2"/>
        <circle cx="18" cy="33" r="2.5" fill="currentColor" class="anim-pulse p3"/>
      `)
    },
    {
      label: 'Purring along...',
      svg: this.wrap(`
        <path d="M10 20l4-12 7 8h6l7-8 4 12"/>
        <path d="M10 20c0 11 6 20 14 20s14-9 14-20"/>
        <circle cx="18" cy="26" r="2" fill="currentColor"/>
        <circle cx="30" cy="26" r="2" fill="currentColor"/>
        <path d="M22 32l2 2 2-2"/>
        <path class="anim-wiggle" d="M14 30l-7 2m7 2l-7 3"/>
        <path class="anim-wiggle wr" d="M34 30l7 2m-7 2l7 3"/>
      `)
    },
    {
      label: 'Tuning in...',
      svg: this.wrap(`
        <path d="M8 28v-6a16 16 0 0132 0v6"/>
        <rect x="4" y="28" width="8" height="12" rx="3"/>
        <rect x="36" y="28" width="8" height="12" rx="3"/>
        <path class="anim-float" d="M23 6a2 2 0 014 0v6a2 2 0 01-4 0z" fill="currentColor" opacity="0.4"/>
        <path class="anim-float" d="M27 9h3" opacity="0.4"/>
      `)
    },
    {
      label: 'On its way...',
      svg: this.wrap(`
        <path d="M6 24l38-16-16 32-4-16z"/>
        <path d="M28 40L44 8"/>
        <path class="anim-trail" d="M4 32l8-2m-8 6l10-2" opacity="0.3"/>
      `)
    },
    {
      label: 'Loading level...',
      svg: this.wrap(`
        <rect x="4" y="14" width="40" height="22" rx="8"/>
        <path d="M16 20v8m-4-4h8"/>
        <circle cx="34" cy="22" r="2.5" fill="currentColor" class="anim-pulse"/>
        <circle cx="38" cy="28" r="2.5" fill="currentColor" class="anim-pulse p2"/>
      `)
    },
    {
      label: 'Rising up...',
      svg: this.wrap(`
        <ellipse cx="24" cy="18" rx="12" ry="14" class="anim-float"/>
        <path d="M15 30l3 6h12l3-6" class="anim-float"/>
        <rect x="18" y="36" width="12" height="6" rx="1"/>
        <path d="M20 30v6m8-6v6"/>
      `)
    },
    {
      label: 'Thinking...',
      svg: this.wrap(`
        <path d="M19 34c-4-3-7-8-7-14a12 12 0 0124 0c0 6-3 11-7 14"/>
        <path d="M19 34h10"/>
        <path d="M20 38h8"/>
        <path d="M21 42h6"/>
        <path class="anim-pulse rays" d="M24 2v3m-16 15H5m38 0h-3m-27-10l-2-2m28 0l2-2" opacity="0.3"/>
      `)
    },
    {
      label: 'Mixing things up...',
      svg: this.wrap(`
        <path d="M18 4h12"/>
        <path d="M20 4v14l-10 22a2 2 0 002 2h24a2 2 0 002-2L28 18V4"/>
        <circle class="anim-bubble b1" cx="22" cy="34" r="2" fill="currentColor" opacity="0.3"/>
        <circle class="anim-bubble b2" cx="28" cy="30" r="1.5" fill="currentColor" opacity="0.3"/>
        <circle class="anim-bubble b3" cx="25" cy="37" r="1" fill="currentColor" opacity="0.3"/>
      `)
    },
    {
      label: 'Warming up...',
      svg: this.wrap(`
        <path d="M12 40l24-4" stroke-width="3"/>
        <path d="M12 36l24 4" stroke-width="3"/>
        <path class="anim-flame f1" d="M24 30c-4-6-2-14 0-18 2 4 4 12 0 18z"/>
        <path class="anim-flame f2" d="M19 32c-2-4-1-9 0-12 1 3 2 8 0 12z" opacity="0.6"/>
        <path class="anim-flame f3" d="M29 32c-2-4-1-9 0-12 1 3 2 8 0 12z" opacity="0.6"/>
      `)
    },
    {
      label: 'Smooth sailing...',
      svg: this.wrap(`
        <path d="M8 34h32l-4 4H12z"/>
        <line x1="24" y1="10" x2="24" y2="34"/>
        <path d="M24 10l14 20H24z"/>
        <path d="M24 14l-8 16h8z" opacity="0.5"/>
        <path class="anim-wave" d="M2 44q6-3 12 0t12 0 12 0" opacity="0.3"/>
      `)
    },
    {
      label: 'Cooking up...',
      svg: this.wrap(`
        <path d="M6 30a18 18 0 0136 0"/>
        <path d="M6 30c5 8 13 10 18 10s13-2 18-10"/>
        <path d="M14 26c3-4 8-6 10-6s7 2 10 6" opacity="0.4"/>
        <circle cx="20" cy="28" r="2" fill="currentColor" opacity="0.3"/>
        <circle cx="28" cy="28" r="2" fill="currentColor" opacity="0.3"/>
      `)
    },
    {
      label: 'Glazing...',
      svg: this.wrap(`
        <circle cx="24" cy="24" r="16"/>
        <circle cx="24" cy="24" r="6"/>
        <path class="anim-pulse" d="M16 12l1 3m10-5l1 3m8 4l-2 2m-20 10l2-1m22 4l-2-1" stroke-width="2.5" opacity="0.4"/>
      `)
    },
    {
      label: 'Beaming down...',
      svg: this.wrap(`
        <ellipse cx="24" cy="20" rx="16" ry="4"/>
        <path d="M14 20c0-6 4-10 10-10s10 4 10 10"/>
        <circle cx="24" cy="14" r="2" fill="currentColor"/>
        <path class="anim-beam" d="M18 24l-4 18h20l-4-18" opacity="0.1" fill="currentColor" stroke="none"/>
        <circle class="anim-beam" cx="24" cy="38" r="2" fill="currentColor" opacity="0.3"/>
      `)
    },
    {
      label: 'Growing...',
      svg: this.wrap(`
        <line x1="24" y1="42" x2="24" y2="18"/>
        <path d="M24 28c-6-1-10-6-10-10 5 1 9 5 10 10z" fill="currentColor" opacity="0.15"/>
        <path d="M24 20c6-1 10-6 10-10-5 1-9 5-10 10z" fill="currentColor" opacity="0.15"/>
        <path d="M24 28c-6-1-10-6-10-10 5 1 9 5 10 10z"/>
        <path d="M24 20c6-1 10-6 10-10-5 1-9 5-10 10z"/>
        <path d="M18 42h12" stroke-width="2.5"/>
        <circle class="anim-float" cx="18" cy="8" r="1.5" fill="currentColor" opacity="0.2"/>
      `)
    },
    {
      label: 'Scooping...',
      svg: this.wrap(`
        <path d="M16 40l4-16h8l4 16z"/>
        <circle cx="24" cy="22" r="6"/>
        <circle cx="18" cy="18" r="5"/>
        <circle cx="30" cy="18" r="5"/>
        <path class="anim-drip" d="M30 26q1 3 0 6" opacity="0.3"/>
      `)
    },
    {
      label: 'Searching...',
      svg: this.wrap(`
        <path d="M24 4c-8 0-12 6-12 12 0 4 1 7 3 10l-2 4 2 8c2 2 6 4 9 4s7-2 9-4l2-8-2-4c2-3 3-6 3-10 0-6-4-12-12-12z" class="anim-float"/>
        <circle cx="20" cy="18" r="2" fill="currentColor"/>
        <circle cx="28" cy="18" r="2" fill="currentColor"/>
        <path d="M20 26c2 2 6 2 8 0" fill="none"/>
        <path d="M18 38q6 4 12 0" opacity="0.3"/>
      `)
    },
    {
      label: 'Brightening up...',
      svg: this.wrap(`
        <circle cx="24" cy="24" r="8"/>
        <path class="anim-pulse rays" d="M24 4v6m0 28v6M4 24h6m28 0h6M10 10l4 4m20 20l4 4M38 10l-4 4M14 34l-4 4" opacity="0.4"/>
      `)
    },
    {
      label: 'Sketching...',
      svg: this.wrap(`
        <path d="M34 6l8 8-24 24H10v-8z"/>
        <path d="M30 10l8 8"/>
        <path class="anim-write" d="M10 42h20" opacity="0.3" stroke-dasharray="20" stroke-dashoffset="20"/>
      `)
    },
    {
      label: 'Happy vibes...',
      svg: this.wrap(`
        <circle cx="24" cy="24" r="18"/>
        <circle cx="17" cy="20" r="2" fill="currentColor"/>
        <circle cx="31" cy="20" r="2" fill="currentColor"/>
        <path d="M16 30c2 4 5 6 8 6s6-2 8-6"/>
      `)
    },
    {
      label: 'Winking...',
      svg: this.wrap(`
        <circle cx="24" cy="24" r="18"/>
        <circle cx="17" cy="20" r="2" fill="currentColor"/>
        <path d="M28 20h6"/>
        <path d="M16 30c2 4 5 6 8 6s6-2 8-6"/>
      `)
    },
    {
      label: 'Cool beans...',
      svg: this.wrap(`
        <circle cx="24" cy="24" r="18"/>
        <path d="M12 18h10m4 0h10"/>
        <rect x="13" y="16" width="8" height="6" rx="2"/>
        <rect x="27" y="16" width="8" height="6" rx="2"/>
        <path d="M18 32c2 3 4 4 6 4s4-1 6-4"/>
      `)
    },
    {
      label: 'Stargazing...',
      svg: this.wrap(`
        <path d="M24 4l3 9h9l-7 5 3 9-8-6-8 6 3-9-7-5h9z"/>
        <circle class="anim-pulse" cx="10" cy="10" r="1.5" fill="currentColor" opacity="0.3"/>
        <circle class="anim-pulse p2" cx="40" cy="14" r="1" fill="currentColor" opacity="0.3"/>
        <circle class="anim-pulse p3" cx="8" cy="38" r="1" fill="currentColor" opacity="0.3"/>
      `)
    },
    {
      label: 'Party time...',
      svg: this.wrap(`
        <path d="M16 4l2 12-10-8z" fill="currentColor" opacity="0.15"/>
        <path d="M16 4l2 12-10-8z"/>
        <path d="M32 4l-2 12 10-8z" fill="currentColor" opacity="0.15"/>
        <path d="M32 4l-2 12 10-8z"/>
        <circle cx="24" cy="30" r="12"/>
        <circle cx="20" cy="28" r="2" fill="currentColor"/>
        <circle cx="28" cy="28" r="2" fill="currentColor"/>
        <path d="M19 34c2 3 4 4 5 4s3-1 5-4"/>
      `)
    },
    {
      label: 'Sending love...',
      svg: this.wrap(`
        <path d="M24 42L6 24c-4-4-4-10 0-14s10-4 14 0l4 4 4-4c4-4 10-4 14 0s4 10 0 14z" class="anim-pulse"/>
      `)
    },
    {
      label: 'High five!',
      svg: this.wrap(`
        <path d="M20 42v-18l-4-10a2 2 0 014 0l2 8"/>
        <path d="M22 22l-1-12a2 2 0 014 0l1 12"/>
        <path d="M26 22l1-10a2 2 0 014 0l-1 10"/>
        <path d="M30 24l2-8a2 2 0 014 0l-2 10"/>
        <path d="M20 24c0 0-2 2-2 6s0 12 0 12h18s2-8 0-14-4-6-4-6"/>
      `)
    },
    {
      label: 'Power up...',
      svg: this.wrap(`
        <path d="M28 4L14 26h10L20 44l14-22H24z" fill="currentColor" opacity="0.1"/>
        <path d="M28 4L14 26h10L20 44l14-22H24z"/>
      `)
    },
    {
      label: 'On a roll...',
      svg: this.wrap(`
        <circle cx="24" cy="24" r="16"/>
        <circle cx="24" cy="24" r="4" fill="currentColor" opacity="0.2"/>
        <circle cx="24" cy="24" r="4"/>
        <circle cx="24" cy="12" r="2" fill="currentColor" class="anim-pulse"/>
        <circle cx="36" cy="24" r="2" fill="currentColor" class="anim-pulse p2"/>
        <circle cx="24" cy="36" r="2" fill="currentColor" class="anim-pulse p3"/>
      `)
    },
    {
      label: 'Dreaming...',
      svg: this.wrap(`
        <circle cx="20" cy="30" r="14"/>
        <circle cx="14" cy="27" r="2" fill="currentColor"/>
        <circle cx="26" cy="27" r="2" fill="currentColor"/>
        <path d="M16 34c1 2 3 3 4 3s3-1 4-3"/>
        <path class="anim-float" d="M34 8a4 4 0 018 0 4 4 0 01-8 0z" opacity="0.3"/>
        <path class="anim-float" d="M38 4a2 2 0 014 0 2 2 0 01-4 0z" opacity="0.2" style="animation-delay:0.4s"/>
        <path class="anim-float" d="M42 2a1 1 0 012 0 1 1 0 01-2 0z" opacity="0.15" style="animation-delay:0.8s"/>
      `)
    },
    {
      label: 'Chilling...',
      svg: this.wrap(`
        <circle cx="24" cy="24" r="18"/>
        <circle cx="17" cy="20" r="2" fill="currentColor"/>
        <circle cx="31" cy="20" r="2" fill="currentColor"/>
        <path d="M17 32h14"/>
      `)
    },
    {
      label: 'Boop!',
      svg: this.wrap(`
        <circle cx="24" cy="26" r="16"/>
        <path d="M18 10l-2-6m16 6l2-6" stroke-width="1.5"/>
        <circle cx="19" cy="23" r="2" fill="currentColor"/>
        <circle cx="29" cy="23" r="2" fill="currentColor"/>
        <ellipse cx="24" cy="30" rx="3" ry="2"/>
        <path class="anim-wiggle" d="M10 26l-6-2" stroke-width="1.5"/>
        <path class="anim-wiggle" d="M10 30l-6 1" stroke-width="1.5"/>
        <path class="anim-wiggle wr" d="M38 26l6-2" stroke-width="1.5"/>
        <path class="anim-wiggle wr" d="M38 30l6 1" stroke-width="1.5"/>
      `)
    },
    {
      label: 'Snacking...',
      svg: this.wrap(`
        <rect x="10" y="14" width="28" height="22" rx="4"/>
        <path d="M10 26h28"/>
        <circle cx="18" cy="20" r="1.5" fill="currentColor"/>
        <circle cx="24" cy="20" r="1.5" fill="currentColor"/>
        <circle cx="30" cy="20" r="1.5" fill="currentColor"/>
        <path class="anim-steam s1" d="M18 10c0-2 2-3 2-5" opacity="0.3"/>
        <path class="anim-steam s2" d="M24 10c0-2 2-3 2-5" opacity="0.3"/>
        <path class="anim-steam s3" d="M30 10c0-2 2-3 2-5" opacity="0.3"/>
      `)
    },
    {
      label: 'Beep boop...',
      svg: this.wrap(`
        <rect x="12" y="8" width="24" height="20" rx="4"/>
        <circle cx="19" cy="18" r="3"/>
        <circle cx="29" cy="18" r="3"/>
        <circle cx="19" cy="18" r="1" fill="currentColor" class="anim-pulse"/>
        <circle cx="29" cy="18" r="1" fill="currentColor" class="anim-pulse p2"/>
        <path d="M18 24h12"/>
        <path d="M16 28v8m16-8v8"/>
        <path d="M14 36h6m8 0h6"/>
        <path d="M12 12h-4m32 0h4" stroke-width="1.5"/>
      `)
    },
    {
      label: 'Making magic...',
      svg: this.wrap(`
        <path d="M8 44L30 10" stroke-width="3"/>
        <path d="M26 4l2 6 6 2-6 2-2 6-2-6-6-2 6-2z" fill="currentColor" opacity="0.2" class="anim-pulse"/>
        <path d="M26 4l2 6 6 2-6 2-2 6-2-6-6-2 6-2z"/>
        <path d="M40 24l1.5 4 4 1.5-4 1.5-1.5 4-1.5-4-4-1.5 4-1.5z" fill="currentColor" opacity="0.2" class="anim-pulse p2"/>
        <path d="M40 24l1.5 4 4 1.5-4 1.5-1.5 4-1.5-4-4-1.5 4-1.5z"/>
      `)
    },
    {
      label: 'Jamming...',
      svg: this.wrap(`
        <circle cx="16" cy="36" r="6"/>
        <circle cx="36" cy="32" r="6"/>
        <path d="M22 36V12l20-4v24"/>
        <path d="M22 20l20-4"/>
        <path class="anim-float" d="M10 8l2-2 2 2 2-2" opacity="0.3"/>
        <path class="anim-float" d="M6 14l2-2 2 2" opacity="0.2" style="animation-delay:0.5s"/>
      `)
    },
    {
      label: 'Zooming...',
      svg: this.wrap(`
        <circle cx="20" cy="20" r="14"/>
        <circle cx="20" cy="20" r="8"/>
        <line x1="30" y1="30" x2="42" y2="42" stroke-width="3"/>
      `)
    },
    {
      label: 'Leveling up...',
      svg: this.wrap(`
        <path d="M24 6l16 28H8z" fill="currentColor" opacity="0.08"/>
        <path d="M24 6l16 28H8z"/>
        <path d="M24 16l8 14H16z"/>
        <circle cx="24" cy="26" r="3" class="anim-pulse"/>
      `)
    },
    {
      label: 'Thumbs up!',
      svg: this.wrap(`
        <path d="M18 22c0-8 4-14 6-16 1 4 2 8-1 12h12a3 3 0 010 6h-1a3 3 0 010 6h-1a3 3 0 010 6H20a4 4 0 01-4-4v-8z"/>
        <rect x="6" y="22" width="8" height="20" rx="2"/>
      `)
    },
    {
      label: 'Orbiting...',
      svg: this.wrap(`
        <circle cx="24" cy="24" r="6"/>
        <ellipse cx="24" cy="24" rx="20" ry="8" transform="rotate(-20 24 24)" fill="none"/>
        <circle cx="42" cy="18" r="3" fill="currentColor" opacity="0.3" class="anim-pulse"/>
      `)
    },
    {
      label: 'Cheers!',
      svg: this.wrap(`
        <path d="M14 16l-4 20h12l-2-20z"/>
        <path d="M34 16l4 20H26l2-20z"/>
        <path d="M20 16c0-6 2-10 4-10s4 4 4 10"/>
        <path d="M28 16c0-6 2-10 4-10"/>
        <path class="anim-pulse" d="M8 8l2 2m28-2l-2 2M24 4v3" opacity="0.3"/>
      `)
    },
    {
      label: 'Unwinding...',
      svg: this.wrap(`
        <path d="M24 8a16 16 0 0116 16" fill="none"/>
        <path d="M24 14a10 10 0 0110 10" fill="none"/>
        <path d="M24 20a4 4 0 014 4" fill="none"/>
        <circle cx="24" cy="24" r="1.5" fill="currentColor"/>
        <circle cx="24" cy="24" r="20" fill="none" opacity="0.2"/>
      `)
    },
    {
      label: 'Tada!',
      svg: this.wrap(`
        <path d="M18 44L6 14l14 8z" fill="currentColor" opacity="0.08"/>
        <path d="M18 44L6 14l14 8z"/>
        <circle cx="16" cy="10" r="2" fill="currentColor" class="anim-pulse"/>
        <circle cx="28" cy="6" r="1.5" fill="currentColor" class="anim-pulse p2"/>
        <circle cx="36" cy="12" r="2" fill="currentColor" class="anim-pulse p3"/>
        <path d="M30 16l4-6m2 10l6-2m-4 10l6 2" opacity="0.3" class="anim-pulse"/>
      `)
    },
    {
      label: 'Nailing it...',
      svg: this.wrap(`
        <path d="M8 24l10 12L40 12" stroke-width="3" fill="none"/>
      `)
    },
    {
      label: 'Keep going...',
      svg: this.wrap(`
        <path d="M4 24h14l4-10 4 20 4-14 4 8h10"/>
      `)
    },
    {
      label: 'Dialing in...',
      svg: this.wrap(`
        <rect x="12" y="4" width="24" height="40" rx="4"/>
        <circle cx="24" cy="34" r="4" class="anim-pulse"/>
        <path d="M20 10h8" opacity="0.3"/>
      `)
    },
    {
      label: 'Treasure hunt...',
      svg: this.wrap(`
        <rect x="8" y="20" width="32" height="22" rx="3"/>
        <path d="M8 20c0-6 4-12 16-12s16 6 16 12"/>
        <circle cx="24" cy="31" r="4"/>
        <path d="M24 27v8m-4-4h8"/>
      `)
    },
    {
      label: 'Abracadabra...',
      svg: this.wrap(`
        <path d="M12 14c0-6 5-10 12-10s12 4 12 10c0 8-12 14-12 20-0-6-12-12-12-20z"/>
        <circle cx="24" cy="40" r="3" fill="currentColor" class="anim-pulse"/>
      `)
    },
    {
      label: 'Cruising...',
      svg: this.wrap(`
        <path d="M6 28l4-12h28l4 12"/>
        <rect x="4" y="28" width="40" height="10" rx="3"/>
        <circle cx="14" cy="38" r="4"/>
        <circle cx="34" cy="38" r="4"/>
        <circle cx="14" cy="38" r="1.5" fill="currentColor"/>
        <circle cx="34" cy="38" r="1.5" fill="currentColor"/>
        <path d="M18 22h4m4 0h4" opacity="0.3"/>
      `)
    }
  ];


  getRandomIcon(): LoadingIcon {
    const index = Math.floor(Math.random() * this.icons.length);
    const colorIndex = Math.floor(Math.random() * this.colors.length);
    return { ...this.icons[index], color: this.colors[colorIndex] };
  }


  private wrap(inner: string): string {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
  }
}
